import type { Severity } from './types';

/**
 * Plain-language help for the validation findings people meet most. The server's own message is always shown
 * unchanged next to its code; this only adds "what it means" and "what to do" in everyday words. A code with no
 * entry here simply shows the server's message.
 */
export interface IssueHelp {
  meaning: string;
  action: string;
}

export const ISSUE_HELP: Record<string, IssueHelp> = {
  EMPLOYEE_MAPPING_MISSING: {
    meaning: 'EPN does not know which of your employees this person in the file is.',
    action: 'Go to Match your employees and choose the matching employee. EPN never matches people by name alone.',
  },
  DUPLICATE_EMPLOYEE_MAPPING: {
    meaning: 'Two people in the file were matched to the same employee.',
    action: 'Go to Match your employees and give each person a different employee.',
  },
  UNBALANCED_PAY_STATEMENT: {
    meaning: "A pay statement's take-home pay does not equal earnings plus reimbursements, minus employee taxes and deductions.",
    action: 'Check the column choices (for example an employer tax chosen as an employee tax) and the file itself, then fix and upload again, or change the column mapping.',
  },
  RECONCILIATION_NOT_POSSIBLE: {
    meaning: 'Your file has no take-home pay column, so EPN cannot double-check its math.',
    action: 'You can continue, but review the totals carefully. Mapping a net pay column lets EPN confirm every statement.',
  },
  MAPPING_ROLE_CONFLICT: {
    meaning: 'A column was given a meaning that looks wrong for its heading, for example an employer tax chosen as an employee tax.',
    action: 'Change the column mapping so each column says what it really is.',
  },
  MAPPING_REQUIRED_ROLE_MISSING: {
    meaning: 'A column EPN needs (employee ID, pay date, pay period start or end, a name, or an earning) was not chosen.',
    action: 'Change the column mapping and choose the missing column.',
  },
  MAPPING_ROLE_UNKNOWN: {
    meaning: 'A column still needs a meaning (for example whether a deduction is taken before or after tax).',
    action: 'Change the column mapping and choose one.',
  },
  MAPPING_ROLE_DUPLICATE: {
    meaning: 'The same meaning (for example "Pay date") was given to more than one column.',
    action: 'Change the column mapping so only one column has it.',
  },
  MAPPING_DUPLICATE_COLUMN: {
    meaning: 'One column was given two meanings.',
    action: 'Change the column mapping so each column has one meaning.',
  },
  SENSITIVE_COLUMN_MAPPED: {
    meaning: 'A column that looks like a Social Security number, bank detail or date of birth was chosen for import. EPN never imports these.',
    action: 'Change the column mapping and choose "Do not import this column" for it.',
  },
  INVALID_STATE_CODE: {
    meaning: 'A state tax needs a recognized two-letter state code (for example CA).',
    action: 'Change the column mapping and enter the state code.',
  },
  INVALID_DATE: {
    meaning: 'A date could not be read. Dates are read as month/day/year (for example 9/25/2025) or 2025-09-25.',
    action: 'Correct the date in your file, or check that the right column was chosen as the date, then upload again.',
  },
  INVALID_DECIMAL: {
    meaning: 'An amount could not be read. Amounts must be plain, non-negative numbers.',
    action: 'Correct the amount in your file, or use the EPN Standard template to record voids and corrections.',
  },
  DUPLICATE_EMPLOYEE_RUN_ROW: {
    meaning: 'The same employee appears more than once in the same pay run.',
    action: 'Combine the rows in your file so there is one row per employee per pay run, then upload again.',
  },
  EARNING_CODE_UNMAPPED: {
    meaning: 'An earning type has no matching EPN earning type. It is kept as written.',
    action: 'Nothing is required. Review it to be sure it is what you expect.',
  },
  TAX_JURISDICTION_UNRECOGNIZED: {
    meaning: 'A tax place (jurisdiction) is not recognized. It is kept as written.',
    action: 'Nothing is required. Review it to be sure it is what you expect.',
  },
  VOIDED_STATEMENT_PRESENT: {
    meaning: 'A voided pay statement is in the file. It is kept for reference and left out of totals.',
    action: 'Nothing is required.',
  },
  CHECKSUM_MISMATCH: {
    meaning: 'A file in the package changed after it was exported.',
    action: 'Export the package again from the source system and upload the new one.',
  },
  DUPLICATE_SOURCE_ID: {
    meaning: 'The same record ID appears twice in the package.',
    action: 'Fix the duplicate in your source system or file and upload again.',
  },
};

export const SEVERITY_EXPLANATION: Record<Severity, string> = {
  BlockingError: 'Errors must be fixed before this import can continue. Nothing is imported while an error remains.',
  Warning: 'Warnings do not stop the import, but please review each one to be sure it is what you expect.',
  Information: 'Information is for your records. No action is needed.',
};

/** Matches the column a server message names: "...(column 'Job Title')". Returns the header or null. */
export function columnNamedBy(message: string): string | null {
  const match = /\(column '(.*)'\)\s*$/.exec(message);
  return match ? match[1]! : null;
}
